/**
 * LivePreviewRenderer -- imperative class that manages a PixiJS display tree
 * for the live preview panel.
 *
 * Unlike SceneRenderer (which renders an infinite editor canvas with grid,
 * gizmos, and selection), this class renders a game-resolution viewport
 * that scales to fit the panel. No editor chrome, no gizmos.
 */
import { Application, Container, Graphics, Text, Texture } from 'pixi.js';
import { useSceneStore } from '@store/sceneStore';
import { useLivePreviewStore } from '@store/livePreviewStore';
import { useSlotGameRuntimeStore } from '@store/slotGameRuntimeStore';
import type { CascadeSlotRuntimeState } from '@store/slotGameRuntimeStore';
import { LiveGameObjectLayer } from './LiveGameObjectLayer';
import { PixiPlayGrid } from '../../cascade-slot-runtime/pixi/PixiPlayGrid';
import type { GridAnimConfig } from '../../cascade-slot-runtime/pixi/PixiPlayGrid';
import { PixiWinOverlay } from '../../cascade-slot-runtime/pixi/PixiWinOverlay';
import { PixiFreeSpinsOverlay } from '../../cascade-slot-runtime/pixi/PixiFreeSpinsOverlay';
import { pixiTextureCache } from '@renderer/pixi/PixiTextureCache';
import { getOrCreateObjectUrl } from '@hooks/useAssetUrl';
import type { CascadeSlotConfig, GridCell } from '@core/types/cascade-slot.types';
import { CascadeGamePhase as Phase } from '@core/types/cascade-slot.types';
import type { GameObject } from '@core/types/gameobject.types';
import { PixiReelSpinOverlay } from './PixiReelSpinOverlay';
import { SpineSymbolManager } from '@renderer/services/cascade-slot/SpineSymbolManager';

export class LivePreviewRenderer {
  private app: Application;
  private backgroundContainer: Container;
  private gameWorldContainer: Container;
  private gameObjectsContainer: Container;

  private gameObjectLayer: LiveGameObjectLayer;
  private unsubscribers: (() => void)[] = [];

  private gameWidth = 720;
  private gameHeight = 1280;
  private isDestroyed = false;
  private syncPending = false;

  // ── Play Mode State ──────────────────────────────────────────────
  private slotPlayOverlay: Container | null = null;
  private playGrid: PixiPlayGrid | null = null;
  private winOverlay: PixiWinOverlay | null = null;
  private freeSpinsOverlay: PixiFreeSpinsOverlay | null = null;
  private runtimeUnsub: (() => void) | null = null;
  private playConfig: CascadeSlotConfig | null = null;
  private playSymbolTextures: Record<string, string> = {};
  private reelSpinOverlay: PixiReelSpinOverlay | null = null;
  private hiddenSymbolIds: Set<string> = new Set();
  private interactiveIds: Map<string, () => void> = new Map();
  private cachedSymbolTextures: Map<string, Texture> = new Map();
  private spineManager: SpineSymbolManager | null = null;

  constructor(app: Application) {
    this.app = app;

    // Build stage hierarchy
    this.backgroundContainer = new Container();
    this.gameWorldContainer = new Container();
    this.gameObjectsContainer = new Container();

    this.gameWorldContainer.addChild(this.gameObjectsContainer);
    this.app.stage.addChild(this.backgroundContainer);
    this.app.stage.addChild(this.gameWorldContainer);

    // Draw background
    this.drawBackground();

    // Create game object layer
    this.gameObjectLayer = new LiveGameObjectLayer(this.gameObjectsContainer);

    // Subscribe to stores
    this.subscribeToStores();

    // Initial sync
    this.updateScaling();
    this.scheduleSyncGameObjects();
  }

  private drawBackground(): void {
    const bg = new Graphics();
    bg.rect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.fill({ color: 0x1a1a2e });
    this.backgroundContainer.addChild(bg);
  }

  private subscribeToStores(): void {
    // Subscribe to ALL scene store changes — the sync method diffs internally
    // via computeVersion, so over-firing is cheap.
    const unsubScene = useSceneStore.subscribe(() => {
      this.scheduleSyncGameObjects();
    });
    this.unsubscribers.push(unsubScene);

    // Subscribe to live preview store changes (viewport, etc.)
    const unsubViewport = useLivePreviewStore.subscribe(() => {
      this.updateScaling();
    });
    this.unsubscribers.push(unsubViewport);
  }

  private scheduleSyncGameObjects(): void {
    if (this.isDestroyed) return;
    // Always mark dirty — even if a sync is in flight
    this.syncPending = true;
    // Only schedule one rAF at a time
    if (!this.syncRunning) {
      requestAnimationFrame(() => this.runSync());
    }
  }

  private syncRunning = false;

  private async runSync(): Promise<void> {
    if (this.isDestroyed) return;
    this.syncRunning = true;
    // Drain all pending syncs
    while (this.syncPending && !this.isDestroyed) {
      this.syncPending = false;
      const { gameObjects, isEffectivelyVisible } = useSceneStore.getState();
      try {
        await this.gameObjectLayer.sync(gameObjects, isEffectivelyVisible);
      } catch (err) {
        console.warn('[LivePreviewRenderer] sync error:', err);
      }
    }
    this.syncRunning = false;
  }

  /** Update the scaling transform to fit game resolution into panel size. */
  updateScaling(): void {
    if (this.isDestroyed) return;

    const viewport = useLivePreviewStore.getState().viewport;
    this.gameWidth = viewport.customWidth || 720;
    this.gameHeight = viewport.customHeight || 1280;

    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    let scale: number;
    if (viewport.fitToPanel) {
      scale = Math.min(screenW / this.gameWidth, screenH / this.gameHeight);
    } else {
      scale = viewport.scale || 1;
    }

    // Center the game world within the panel
    this.gameWorldContainer.scale.set(scale);
    this.gameWorldContainer.position.set(
      (screenW - this.gameWidth * scale) / 2,
      (screenH - this.gameHeight * scale) / 2,
    );

    // Offset the game objects container so (0,0) is at the center of the
    // game area -- matching the scene view's center-origin convention.
    this.gameObjectsContainer.position.set(
      this.gameWidth / 2,
      this.gameHeight / 2,
    );
  }

  /** Called when the PixiJS app is resized. */
  resize(width: number, height: number): void {
    this.app.renderer.resize(width, height);

    // Redraw background to cover the new size
    this.backgroundContainer.removeChildren();
    this.drawBackground();

    this.updateScaling();
  }

  /** Get the PixiJS Application instance. */
  getApp(): Application {
    return this.app;
  }

  /** Get the game objects container (for external access if needed). */
  getGameObjectsContainer(): Container {
    return this.gameObjectsContainer;
  }

  // ── Play Mode ───────────────────────────────────────────────────

  /**
   * Enter or exit play mode. When active, a PixiPlayGrid overlay is added
   * on top of the hierarchy-rendered game objects, symbol sprite placeholders
   * are hidden, and buttons become interactive.
   */
  setPlayMode(
    active: boolean,
    config: CascadeSlotConfig | null,
    symbolTextures: Record<string, string>,
  ): void {
    if (active && config) {
      this.enterPlayMode(config, symbolTextures);
    } else {
      this.exitPlayMode();
    }
  }

  private enterPlayMode(
    config: CascadeSlotConfig,
    symbolTextures: Record<string, string>,
  ): void {
    this.playConfig = config;
    this.playSymbolTextures = symbolTextures;

    // Create SpineSymbolManager and preload spine symbols (async, non-blocking)
    const hasSpineSymbols = config.symbols.some(s => s.displayMode === 'spine' && s.spineConfig);
    if (hasSpineSymbols) {
      this.spineManager = new SpineSymbolManager();
      this.spineManager.preload(config.symbols).catch(err => {
        console.warn('[LivePreviewRenderer] Spine preload failed:', err);
      });
      if (config.spineTimeScale != null) {
        this.spineManager.setGlobalTimeScale(config.spineTimeScale);
      }
    }

    // Create overlay container
    this.slotPlayOverlay = new Container();
    this.slotPlayOverlay.sortableChildren = true;
    this.gameObjectsContainer.addChild(this.slotPlayOverlay);

    // Find slot-machine position in hierarchy
    const gameObjects = useSceneStore.getState().gameObjects;
    const slotMachineObj = Object.values(gameObjects).find(
      go => go.name === 'slot-machine',
    );
    const gridWorldPos = slotMachineObj
      ? this.getWorldPos(slotMachineObj, gameObjects)
      : { x: 0, y: -55 };

    // Create PixiPlayGrid
    const gridWidth = 680;
    const gridHeight = 580;
    this.playGrid = new PixiPlayGrid();
    this.playGrid.setup(config, gridWidth, gridHeight, this.spineManager ?? undefined);
    this.playGrid.container.position.set(gridWorldPos.x, gridWorldPos.y);
    this.playGrid.container.zIndex = 10;
    this.slotPlayOverlay.addChild(this.playGrid.container);

    // Create win overlay
    this.winOverlay = new PixiWinOverlay(gridWidth, gridHeight);
    this.winOverlay.container.position.set(gridWorldPos.x, gridWorldPos.y);
    this.winOverlay.container.zIndex = 20;
    this.slotPlayOverlay.addChild(this.winOverlay.container);

    // Create free spins overlay
    this.freeSpinsOverlay = new PixiFreeSpinsOverlay();
    this.freeSpinsOverlay.container.position.set(
      gridWorldPos.x,
      gridWorldPos.y - gridHeight / 2 - 30,
    );
    this.freeSpinsOverlay.container.zIndex = 30;
    this.slotPlayOverlay.addChild(this.freeSpinsOverlay.container);

    // Hide symbol sprite placeholders in LiveGameObjectLayer
    this.hideSymbolPlaceholders(gameObjects);

    // Subscribe to runtime store
    this.runtimeUnsub = useSlotGameRuntimeStore.subscribe(
      (state, prevState) => this.onRuntimeStateChange(state, prevState),
    );

    // Do initial grid sync
    const initialState = useSlotGameRuntimeStore.getState();
    if (initialState.grid && initialState.grid.length > 0) {
      this.syncGrid(initialState.grid);
    }
  }

  private exitPlayMode(): void {
    // Unsubscribe
    if (this.runtimeUnsub) {
      this.runtimeUnsub();
      this.runtimeUnsub = null;
    }

    // Clean up reel spin overlay
    this.cleanupReelSpinOverlay();

    // Destroy overlay
    if (this.slotPlayOverlay) {
      this.gameObjectsContainer.removeChild(this.slotPlayOverlay);
      this.slotPlayOverlay.destroy({ children: true });
      this.slotPlayOverlay = null;
    }
    this.playGrid = null;
    this.winOverlay = null;
    this.freeSpinsOverlay = null;
    this.playConfig = null;

    // Clean up SpineSymbolManager
    if (this.spineManager) {
      this.spineManager.dispose();
      this.spineManager = null;
    }

    // Unhide symbol sprites
    for (const id of this.hiddenSymbolIds) {
      this.gameObjectLayer.setVisible(id, true);
    }
    this.hiddenSymbolIds.clear();

    // Remove button interactivity
    for (const [id] of this.interactiveIds) {
      this.gameObjectLayer.removeInteractive(id);
    }
    this.interactiveIds.clear();

    // Re-sync to restore full static view
    this.scheduleSyncGameObjects();
  }

  // ── Runtime State Sync ─────────────────────────────────────────

  private onRuntimeStateChange(
    state: CascadeSlotRuntimeState,
    prevState: CascadeSlotRuntimeState,
  ): void {
    if (this.isDestroyed || !this.playGrid) return;

    // Game phase changed — handle reel spin animation
    if (state.gamePhase !== prevState.gamePhase) {
      this.handlePhaseChange(state.gamePhase, prevState.gamePhase, state);
    }

    // Force-stop requested during reel spin
    if (state.skipAnimations && !prevState.skipAnimations && this.reelSpinOverlay) {
      this.reelSpinOverlay.forceStop();
    }

    // Grid changed (cascade animations via PixiPlayGrid)
    if (state.grid !== prevState.grid) {
      // During ReelSpinning, the overlay handles rendering — don't sync grid
      if (state.gamePhase !== Phase.ReelSpinning) {
        this.syncGrid(state.grid);
      }
    }

    // Update hierarchy text displays
    if (state.balance !== prevState.balance) {
      this.updateDisplayText(
        'balance-ui',
        'Amount',
        this.formatCurrency(state.balance),
      );
    }
    if (
      state.totalSpinWin !== prevState.totalSpinWin ||
      state.currentSpinWin !== prevState.currentSpinWin
    ) {
      const winDisplay = state.totalSpinWin || state.currentSpinWin || 0;
      this.updateDisplayText(
        'win-ui',
        'Amount',
        this.formatCurrency(winDisplay),
      );
    }
    if (state.currentBet !== prevState.currentBet) {
      this.updateDisplayText(
        'bet-option-button',
        'Label',
        `Bet: ${state.currentBet.toLocaleString()}`,
      );
    }

    // Free spins
    if (
      state.freeSpinsActive !== prevState.freeSpinsActive ||
      state.freeSpinsRemaining !== prevState.freeSpinsRemaining
    ) {
      if (this.freeSpinsOverlay) {
        if (state.freeSpinsActive) {
          this.freeSpinsOverlay.show(
            state.freeSpinsRemaining,
            state.activeMultiplier,
          );
        } else {
          this.freeSpinsOverlay.hide();
        }
      }
      // Show/hide free spin container in hierarchy
      const gameObjects = useSceneStore.getState().gameObjects;
      const freeSpinObj = Object.values(gameObjects).find(
        go => go.name === 'free-spin-container',
      );
      if (freeSpinObj) {
        this.gameObjectLayer.setVisible(freeSpinObj.id, state.freeSpinsActive);
        if (state.freeSpinsActive) {
          const counterObj = Object.values(gameObjects).find(
            go => go.name === 'counter' && go.parentId === freeSpinObj.id,
          );
          if (counterObj) {
            this.updateDisplayTextById(
              counterObj.id,
              `FREE SPIN: ${state.freeSpinsRemaining}`,
            );
          }
        }
      }
    }

    // Win overlay
    if (state.lastWins !== prevState.lastWins && this.winOverlay) {
      if (state.lastWins && state.lastWins.length > 0) {
        const totalWin = state.lastWins.reduce((sum, w) => sum + w.payout, 0);
        this.winOverlay.show(
          totalWin,
          state.activeMultiplier,
          state.lastWins.length,
        );
      } else {
        this.winOverlay.hide();
      }
    }
  }

  private handlePhaseChange(
    phase: string,
    prevPhase: string,
    state: CascadeSlotRuntimeState,
  ): void {
    if (phase === Phase.ReelSpinning && prevPhase !== Phase.ReelSpinning) {
      // Entering reel spin — stop spine animations, start reel overlay
      this.spineManager?.stopAllAnimations();
      this.startReelSpinAnimation(state);
    } else if (prevPhase === Phase.ReelSpinning && phase !== Phase.ReelSpinning) {
      // Exiting reel spin — clean up overlay, show play grid again
      this.cleanupReelSpinOverlay();
      if (this.playGrid) {
        this.playGrid.container.visible = true;
      }
      // Restart idle animations on wild/scatter symbols
      this.playGrid?.triggerIdleAnimations();
    }

    if (phase === Phase.Spinning) {
      // Hide win overlay when new spin starts
      if (this.winOverlay) this.winOverlay.hide();
    }

    // Spine animation orchestration per phase
    if (phase === Phase.WinPresenting && this.playGrid && this.playConfig) {
      // Trigger prepare→match sequence on winning symbols
      const winPositions: Array<{ row: number; col: number }> = [];
      if (state.lastWins) {
        for (const win of state.lastWins) {
          for (const pos of win.positions) {
            winPositions.push(pos);
          }
        }
      }
      if (winPositions.length > 0) {
        this.playGrid.triggerWinAnimations(
          winPositions,
          this.playConfig.cascadeColumnDelay,
        );
      }
    }

    if (phase === Phase.Idle && prevPhase !== Phase.Idle) {
      // Restart idle animations on wild/scatter
      this.playGrid?.triggerIdleAnimations();
    }
  }

  private async startReelSpinAnimation(state: CascadeSlotRuntimeState): Promise<void> {
    if (!this.playConfig || !this.slotPlayOverlay) return;

    // Load symbol textures (by symbolId, not textureAssetPath) for the reel overlay
    const symbolTextures = new Map<string, Texture>();
    for (const sym of this.playConfig.symbols) {
      const path = this.playSymbolTextures[sym.id] ?? sym.textureAssetPath;
      if (path) {
        try {
          const blobUrl = await getOrCreateObjectUrl(path);
          const texture = await pixiTextureCache.getTexture(blobUrl);
          symbolTextures.set(sym.id, texture);
        } catch { /* color fallback */ }
      }
    }
    this.cachedSymbolTextures = symbolTextures;

    // Create reel spin overlay
    const gridWidth = 680;
    const gridHeight = 580;

    this.reelSpinOverlay = new PixiReelSpinOverlay();
    this.reelSpinOverlay.setup(this.playConfig, gridWidth, gridHeight);

    // Position same as play grid
    const gameObjects = useSceneStore.getState().gameObjects;
    const slotMachineObj = Object.values(gameObjects).find(
      go => go.name === 'slot-machine',
    );
    const gridWorldPos = slotMachineObj
      ? this.getWorldPos(slotMachineObj, gameObjects)
      : { x: 0, y: -55 };

    // Offset to top-left of grid area (play grid is centered, overlay is top-left aligned)
    this.reelSpinOverlay.container.position.set(
      gridWorldPos.x - gridWidth / 2,
      gridWorldPos.y - gridHeight / 2,
    );
    this.reelSpinOverlay.container.zIndex = 15; // Above play grid (10), below win overlay (20)
    this.slotPlayOverlay.addChild(this.reelSpinOverlay.container);

    // Hide play grid during spin
    if (this.playGrid) {
      this.playGrid.container.visible = false;
    }

    // Get current grid for initial symbol display
    const currentGrid = state.grid;
    const turbo = state.turbo;

    // Start the spin
    this.reelSpinOverlay.startSpin(
      currentGrid,
      symbolTextures,
      {
        turboMultiplier: turbo ? 2 : 1,
        minimumSpinDuration: turbo ? 0.5 : 1.0,
      },
      () => {
        // onComplete: signal engine that reels have stopped
        useSlotGameRuntimeStore.getState().setReelSpinComplete(true);
      },
    );

    // Check if result grid is already available (API may have responded fast)
    const resultGrid = useSlotGameRuntimeStore.getState().reelSpinResultGrid;
    if (resultGrid) {
      this.reelSpinOverlay.setResult(resultGrid, symbolTextures);
    } else {
      // Watch for result grid to arrive
      const unsub = useSlotGameRuntimeStore.subscribe((s, prev) => {
        if (s.reelSpinResultGrid && !prev.reelSpinResultGrid && this.reelSpinOverlay) {
          this.reelSpinOverlay.setResult(s.reelSpinResultGrid, this.cachedSymbolTextures);
          unsub();
        }
        // Also handle force-stop while waiting for result
        if (s.skipAnimations && !prev.skipAnimations && this.reelSpinOverlay) {
          this.reelSpinOverlay.forceStop();
          unsub();
        }
      });
    }
  }

  private cleanupReelSpinOverlay(): void {
    if (this.reelSpinOverlay) {
      this.reelSpinOverlay.destroy();
      if (this.slotPlayOverlay) {
        this.slotPlayOverlay.removeChild(this.reelSpinOverlay.container);
      }
      this.reelSpinOverlay = null;
    }
  }

  private async syncGrid(grid: GridCell[][]): Promise<void> {
    if (!this.playGrid || !this.playConfig) return;

    // Load textures — the PixiPlayGrid expects textures keyed by textureAssetPath
    const textureMap = new Map<string, Texture>();
    for (const sym of this.playConfig.symbols) {
      const path =
        this.playSymbolTextures[sym.id] ?? sym.textureAssetPath;
      if (path) {
        try {
          const blobUrl = await getOrCreateObjectUrl(path);
          const texture = await pixiTextureCache.getTexture(blobUrl);
          if (sym.textureAssetPath) {
            textureMap.set(sym.textureAssetPath, texture);
          }
        } catch {
          /* color fallback */
        }
      }
    }

    const runtimeState = useSlotGameRuntimeStore.getState();
    const turbo = runtimeState.turbo;
    const skip = runtimeState.skipAnimations;

    const animConfig: GridAnimConfig = {
      removeDuration: turbo ? 150 : 300,
      fillDuration: turbo ? 200 : 400,
      gravityDuration: turbo ? 250 : 500,
      columnDelay: turbo ? 30 : 60,
      winScale: 1.15,
      maxFallDistance: this.playConfig.rows ?? 4,
      fallStyle: 'bounce' as const,
      skipAnimations: skip || false,
    };

    this.playGrid.updateGrid(grid, textureMap, animConfig);
  }

  // ── Play Mode Helpers ──────────────────────────────────────────

  private hideSymbolPlaceholders(
    gameObjects: Record<string, GameObject>,
  ): void {
    // Find all symbol sprites inside reel containers and hide them
    const reelContainerObj = Object.values(gameObjects).find(
      go => go.name === 'reelContainer',
    );
    if (!reelContainerObj) return;

    const hideChildren = (parentId: string) => {
      const parent = gameObjects[parentId];
      if (!parent) return;
      for (const childId of parent.childIds) {
        const child = gameObjects[childId];
        if (!child) continue;
        if (child.type === 'sprite') {
          this.gameObjectLayer.setVisible(childId, false);
          this.hiddenSymbolIds.add(childId);
        }
        if (child.childIds.length > 0) {
          hideChildren(childId);
        }
      }
    };
    hideChildren(reelContainerObj.id);
  }

  private updateDisplayText(
    containerName: string,
    childName: string,
    text: string,
  ): void {
    const gameObjects = useSceneStore.getState().gameObjects;
    const container = Object.values(gameObjects).find(
      go => go.name === containerName,
    );
    if (!container) return;
    const child = Object.values(gameObjects).find(
      go => go.name === childName && go.parentId === container.id,
    );
    if (!child) return;
    this.updateDisplayTextById(child.id, text);
  }

  private updateDisplayTextById(id: string, text: string): void {
    const rendered = this.gameObjectLayer.getDisplayObject(id);
    if (!rendered) return;
    // Find Text child in the rendered container
    for (const child of rendered.children) {
      if (child instanceof Text) {
        child.text = text;
        return;
      }
    }
  }

  makeButtonInteractive(gameObjectId: string, onClick: () => void): void {
    this.gameObjectLayer.setInteractive(gameObjectId, onClick);
    this.interactiveIds.set(gameObjectId, onClick);
  }

  private getWorldPos(
    obj: GameObject,
    gameObjects: Record<string, GameObject>,
  ): { x: number; y: number } {
    let x = obj.transform.position.x;
    let y = obj.transform.position.y;
    let current = obj;
    while (current.parentId) {
      const parent = gameObjects[current.parentId];
      if (!parent) break;
      x += parent.transform.position.x;
      y += parent.transform.position.y;
      current = parent;
    }
    return { x, y };
  }

  private formatCurrency(amount: number): string {
    return `Rp${amount.toLocaleString()}`;
  }

  // ── Lifecycle ──────────────────────────────────────────────────

  destroy(): void {
    this.exitPlayMode(); // Clean up play mode first
    this.isDestroyed = true;

    // Unsubscribe from stores first
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];

    // Destroy sub-systems
    this.gameObjectLayer.destroy();

    // Destroy containers
    this.backgroundContainer.destroy({ children: true });
    this.gameWorldContainer.destroy({ children: true });
  }
}
